import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../../lib/api";
import { useLang } from "../../contexts/LanguageContext";
import PageHero from "../../components/public/PageHero";
import { usePageSeo, htmlToText } from "../../components/public/seo/PageSeo";
import { ArrowLeft, MapPin, Calendar as CalendarIcon, Share2 } from "lucide-react";
import { embedVideosInHtml, videoEmbedUrl } from "../../lib/videoEmbeds";

// Localised chip labels for the event categories.
const CATEGORY_LABEL = {
  encuentros:            { es: "Encuentros empresariales", fr: "Rencontres d'affaires" },
  networking:            { es: "Networking", fr: "Networking" },
  seminarios:            { es: "Seminarios", fr: "Séminaires" },
  salones_ferias:        { es: "Salones, ferias y foros", fr: "Salons, foires et forums" },
  visitas_reuniones:     { es: "Visitas y reuniones", fr: "Visites et réunions" },
  actos_institucionales: { es: "Actos institucionales", fr: "Actes institutionnels" },
  webinars:              { es: "Webinars", fr: "Webinaires" },
  actividad:             { es: "Actividad", fr: "Activité" },
  camara:                { es: "Eventos Cámara", fr: "Événements Chambre" },
  socios:                { es: "Eventos Socios", fr: "Événements Membres" },
  ferias_es:             { es: "Ferias España", fr: "Salons Espagne" },
  ferias_ma:             { es: "Ferias Marruecos", fr: "Salons Maroc" },
};

/**
 * Detail page for a single event / activity.
 * Route: /actividades/:slug
 *
 * Renders `description_es|fr` as rich HTML (paragraphs, headings, optional
 * YouTube embed) via dangerouslySetInnerHTML. Content is scraped/curated by
 * the CMS so we control what's stored.
 */
export default function EventoDetail() {
  const { slug } = useParams();
  const { pick, t, lang } = useLang();
  const [item, setItem] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/events/${slug}`).then((r) => setItem(r.data)).catch(() => setError(true));
  }, [slug]);

  // Per-page SEO override: use meta_description_{lang} if set, else derive
  // a text excerpt from the rich description body.
  usePageSeo(item ? {
    title: `${pick(item, "title")} · CECIT`,
    description: pick(item, "meta_description") || htmlToText(pick(item, "description")),
    image: item.cover_image || null,
    type: "article",
  } : null);

  if (error) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-gray-500">
        <p className="mb-4">{lang === "es" ? "Actividad no encontrada" : "Activité introuvable"}</p>
        <Link to="/actividades" className="text-[#C8102E] hover:underline">
          {lang === "es" ? "Volver a Actividades" : "Retour aux Activités"}
        </Link>
      </div>
    );
  }
  if (!item) {
    return <div className="min-h-[60vh] flex items-center justify-center text-gray-400">…</div>;
  }

  const dateFmt = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      // If time is 00:00 UTC, show date only (activities we scraped are date-only).
      const hasTime = d.getUTCHours() !== 0 || d.getUTCMinutes() !== 0;
      const opts = { day: "numeric", month: "long", year: "numeric" };
      if (hasTime) { opts.hour = "2-digit"; opts.minute = "2-digit"; }
      return d.toLocaleString(lang === "es" ? "es-ES" : "fr-FR", opts);
    } catch { return iso; }
  };

  const raw = (pick(item, "description") || "").trim();
  const looksLikeHtml = /<(p|h[1-6]|ul|ol|div|iframe|blockquote)\b/i.test(raw);
  const bodyHtml = looksLikeHtml ? embedVideosInHtml(raw) : raw;

  const share = async () => {
    const url = window.location.href;
    const title = pick(item, "title");
    try {
      if (navigator.share) await navigator.share({ title, url });
      else { await navigator.clipboard.writeText(url); alert(lang === "es" ? "Enlace copiado" : "Lien copié"); }
    } catch { /* user cancelled */ }
  };

  return (
    <div data-testid={`evento-detail-${slug}`}>
      <PageHero title={pick(item, "title")} subtitle={item.location} image={item.cover_image} />

      <article className="max-w-3xl mx-auto px-6 py-16">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <Link
            to="/actividades"
            className="inline-flex items-center gap-2 text-sm text-[#0F2040] hover:text-[#C8102E] transition-colors"
            data-testid="evento-back"
          >
            <ArrowLeft className="w-4 h-4" /> {t("common.back")}
          </Link>
          <button
            type="button"
            onClick={share}
            className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-[#C8102E] transition-colors"
            data-testid="evento-share"
          >
            <Share2 className="w-4 h-4" /> {lang === "es" ? "Compartir" : "Partager"}
          </button>
        </div>

        {/* Meta */}
        <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-gray-600 mb-10 pb-6 border-b border-gray-100">
          {item.starts_at && (
            <span className="inline-flex items-center gap-2">
              <CalendarIcon className="w-4 h-4 text-[#C8102E]" />
              {dateFmt(item.starts_at)}
              {item.ends_at ? <> — {dateFmt(item.ends_at)}</> : null}
            </span>
          )}
          {item.location && (
            <span className="inline-flex items-center gap-2">
              <MapPin className="w-4 h-4 text-[#C8102E]" />
              {item.location}
            </span>
          )}
          {item.category && (
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#C8102E] font-semibold">
              {CATEGORY_LABEL[item.category]?.[lang] || item.category.replace(/_/g, " ")}
            </span>
          )}
        </div>

        {/* Body */}
        {looksLikeHtml ? (
          <div
            className="prose-cecit-article"
            dangerouslySetInnerHTML={{ __html: bodyHtml }}
            data-testid="evento-body-html"
          />
        ) : (
          <div className="prose-cecit-article" data-testid="evento-body-text">
            {raw.split(/\n\n+/).map((p, i) => {
              const txt = p.trim();
              const src = /^https?:\/\/\S+$/.test(txt) ? videoEmbedUrl(txt) : null;
              if (src) {
                return (
                  <div className="video-embed" key={i}>
                    <iframe src={src} title="Video" loading="lazy" allowFullScreen
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" />
                  </div>
                );
              }
              return <p key={i}>{p}</p>;
            })}
          </div>
        )}

        {/* Back link at the bottom too, for long articles */}
        <div className="mt-14 pt-8 border-t border-gray-100">
          <Link
            to="/actividades"
            className="inline-flex items-center gap-2 text-sm text-[#0F2040] hover:text-[#C8102E] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> {lang === "es" ? "Todas las actividades" : "Toutes les activités"}
          </Link>
        </div>
      </article>
    </div>
  );
}
